import { useEffect, useRef, useState } from 'react';
import { Redirect, useParams } from 'react-router-dom';
import Helmet from '../../../../components/Helmet';
import { createContactUsModal } from '../../../../components/ContactUs';
import { srmAsset } from '../data/assets';
import { sceneMap } from '../data/cn/scenes';
import type { SrmSceneKey } from '../data/types';
import { SRM_SCENE_TDK } from '../seo-tdk';
import useCeilingTheme from '../hooks/useCeilingTheme';
import useReveal from '../hooks/useReveal';
// 样式直接取自 demo（AI-SRM-scene-working.html），由 scripts/extract-demo-css.mjs 生成
import '../styles/demo-scene.css';
import '../styles/overrides-scene.css';

const ROOT = '.srm-scene-page';
const DEFAULT_SCENE_KEY: SrmSceneKey = 'collective-procurement';
const DEFAULT_SCENE_PATH = `/product/ai-srm/scene/${DEFAULT_SCENE_KEY}`;

/** demo 中场景 6（AI 原生方式）的演示区标题与其它场景不同 */
const DEMO_TITLE_BY_KEY: Partial<Record<SrmSceneKey, string>> = {
  'ai-native': '技术平台底座'
};

const isSceneKey = (key?: string): key is SrmSceneKey =>
  !!key && Object.prototype.hasOwnProperty.call(sceneMap, key);

export default function AiSrmScene() {
  const { sceneKey } = useParams<{ sceneKey?: string }>();
  const [active, setActive] = useState(0);
  const [changing, setChanging] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const scene = isSceneKey(sceneKey) ? sceneMap[sceneKey] : undefined;

  useCeilingTheme('.scene-hero');
  useReveal(ROOT);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
  }, [sceneKey]);

  useEffect(() => {
    setActive(0);
  }, [sceneKey]);

  if (!scene) {
    // react-router v5 的 <Redirect> 默认走 history.replace
    return <Redirect to={DEFAULT_SCENE_PATH} />;
  }

  const seo = SRM_SCENE_TDK[scene.key];
  const activeIndex = active < scene.demos.length ? active : 0;
  const activeDemo = scene.demos[activeIndex];

  // 与 demo 的 showDemo() 一致：切换演示项时显式触发播放，
  // 仅靠 autoPlay 在限制自动播放的浏览器（如 iPhone 低电量模式）下可能停在首帧
  useEffect(() => {
    // demo 在切换时短暂加 .is-changing（图片降透明度过渡），180ms 后移除
    setChanging(true);
    const timer = window.setTimeout(() => setChanging(false), 180);

    const video = videoRef.current;
    if (video) {
      const playResult = video.play();
      if (playResult && typeof playResult.catch === 'function') {
        playResult.catch(() => {});
      }
    }

    return () => window.clearTimeout(timer);
  }, [activeIndex, scene.key]);

  return (
    <div className="srm-scene-page" data-scene={scene.order}>
      <Helmet title={seo.title} description={seo.description} keywords={seo.keywords} />

      <main>
        <section className="scene-hero" style={{ backgroundImage: `url("${scene.hero.url}")` }}>
          <div className="wrap hero-grid">
            <h1 id="sceneTitle">{scene.name}</h1>
            <p className="scene-subtitle" id="sceneSubtitle">
              {scene.subtitle}
            </p>
            <div className="hero-overview">
              <p className="hero-summary" id="sceneSummary">
                {scene.summary}
              </p>
            </div>
          </div>
        </section>

        {/* 关键能力 */}
        <section className="section" id="value">
          <div className="wrap">
            <div className="section-head">
              <div>
                <h2>关键能力</h2>
              </div>
            </div>
            <div className="value-grid" id="valueGrid">
              {scene.values.map((value) => (
                <article className="value-card" key={value.title}>
                  <i className="value-icon">
                    <img src={srmAsset(`icons/${value.icon}.png`)} alt="" />
                  </i>
                  <b>{value.title}</b>
                  <p>{value.desc}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* 将智能真正融入业务流程（demo 最终只显示整图） */}
        <section className="section apps-section">
          <div className="wrap">
            <div className="section-head">
              <div>
                <h2>将智能真正融入业务流程</h2>
              </div>
            </div>
            <div className="apps-panel">
              <figure className="apps-media">
                {/* 移动端（≤768px）改用 16:9 手机适配稿，桌面端保持原图不变 */}
                <picture>
                  <source media="(max-width: 768px)" srcSet={scene.processMobile.url} />
                  <img id="appsImage" src={scene.process.url} alt={scene.process.alt} />
                </picture>
              </figure>
            </div>
          </div>
        </section>

        {/* 智能应用演示 */}
        <section className="section demo-section" id="demo">
          <div className="wrap">
            <div className="section-head">
              <div>
                <h2 id="demoSectionTitle">{DEMO_TITLE_BY_KEY[scene.key] || '智能应用演示'}</h2>
              </div>
              <p>点击观看demo</p>
            </div>
            <div className="demo-shell">
              <div className="demo-tabs" id="demoTabs" role="tablist">
                {scene.demos.map((demo, index) => (
                  <button
                    key={demo.title}
                    className={`demo-tab${index === activeIndex ? ' is-active' : ''}`}
                    type="button"
                    role="tab"
                    aria-selected={index === activeIndex}
                    data-index={index}
                    onClick={() => setActive(index)}
                  >
                    <b>{demo.title}</b>
                  </button>
                ))}
              </div>
              <div
                className={`demo-stage${changing ? ' is-changing' : ''}${
                  activeDemo.media.type === 'video' ? ' has-video' : ' has-image'
                }`}
                id="demoStage"
              >
                <div className="demo-visual">
                  <div className="browser-frame">
                    <div className="demo-image-wrap" id="demoImageWrap">
                      {activeDemo.media.type === 'video' ? (
                        <video
                          key={activeDemo.media.url}
                          ref={videoRef}
                          id="demoVideo"
                          className="demo-video"
                          src={activeDemo.media.url}
                          autoPlay
                          muted
                          loop
                          playsInline
                          preload="metadata"
                          style={{ display: 'block' }}
                        />
                      ) : (
                        <img
                          key={activeDemo.media.url}
                          id="demoImage"
                          className="demo-image"
                          src={activeDemo.media.url}
                          alt={`${activeDemo.title} 演示画面`}
                          style={{ display: 'block' }}
                        />
                      )}
                      <div className="demo-scan" />
                    </div>
                    <div className="demo-placeholder" id="demoPlaceholder">
                      <div>
                        <span>＋</span>
                        <b>演示位置已预留</b>
                        <p>待补充真实系统截图、操作录屏或交互 Demo。</p>
                      </div>
                    </div>
                  </div>
                </div>
                <article className="demo-copy">
                  <h3 id="demoTitle">{activeDemo.title}</h3>
                  <p className="demo-lead" id="demoLead">
                    {activeDemo.lead}
                  </p>
                  <ul id="demoBullets">
                    {activeDemo.bullets.map((bullet) => (
                      <li key={bullet}>{bullet}</li>
                    ))}
                  </ul>
                  <div className="demo-value">
                    <strong id="demoValue">{activeDemo.value}</strong>
                  </div>
                </article>
              </div>
            </div>
          </div>
        </section>

        {/* 底部转化 */}
        <section className="bottom-cta" id="contact">
          <div className="wrap">
            <div className="bottom-box">
              <div>
                <h2>让智慧采购进入真实业务现场</h2>
                <p>告诉我们您的采购场景，我们将为您设计可试点、可集成、可演进的演示方案。</p>
              </div>
              <a
                className="btn"
                href="#contact"
                onClick={(event) => {
                  event.preventDefault();
                  createContactUsModal();
                }}
              >
                联系我们
              </a>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
